import React, {useCallback, useState} from "react"
import {HeaderComponentProps, PaginatedSelector} from "@/components/paginated-selector/paginated-selector";
import {apiV2} from "@/api/axiosConfig";
import {convertV1ToV2, PaginatedResponse} from "@/types/PaginatedResponse";
import {useAuth} from "@/context/user";
import {ClientFilter} from "@/components/paginated-selector/filters/ClientFilter";
import {DepartmentFilter} from "@/components/paginated-selector/filters/DepartmentFilter";
import {FilterQueryParams} from "@/components/paginated-selector/filters/types";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import * as XLSX from "xlsx";
import {DepartmentName} from "@/lib/departments/DepartmentName";

type Facility = {
    id: string;
    name: string;
    clientID: string;
    clientName: string;
    departmentID?: string;
    address: {
        countryISO: string;
        countryName: string;
        region: string;
        county: string;
        town: string;
        postalCode: string;
        addressLines: string[];
        formatted: string;
    };
    position: {
        type: string;
        coordinates: [number, number]; // [longitude, latitude]
    };
    guardianIDs: string[];
}

type FacilitySelectorProps = {
    renderActions?: (facility: Facility) => React.ReactNode;
    onColumnClick?: (facility: Facility, columnIndex: number) => void;
}

export function FacilitySelector({renderActions, onColumnClick}: FacilitySelectorProps) {
    const {isSuperAdmin} = useAuth();
    const [exporting, setExporting] = useState(false);

    const headers = [
        "Name",
        ...(isSuperAdmin ? ["Client"] : []),
        "Department",
        "Address"
    ];

    const renderHeader = useCallback((props: HeaderComponentProps) => {
        if (headers[props.columnIndex] == "Client" && isSuperAdmin) {
            return <ClientFilter {...props} />;
        }
        if (headers[props.columnIndex] == "Department" && (!isSuperAdmin || props.filterParams.clientID)) {
            return <DepartmentFilter {...props} clientID={props.filterParams.clientID as string | undefined} />;
        }
        return props.label;
    }, [isSuperAdmin]);

    // Client col (1) only clickable for super admins; department col (2) never clickable
    const nonClickableColumns = isSuperAdmin ? [2] : [1, 2];

    const getFacilities = useCallback(async (search: string, page: number, filterParams?: FilterQueryParams) => {
        const params: any = {
            search: search,
            page: page,
            limit: 10,
            ...filterParams,
        }

        const res = await apiV2.get<PaginatedResponse<any>>("facilities", {params})

        return convertV1ToV2(res.data, 10)
    }, [])

    const getColumns = useCallback((facility: Facility) => {
        return [
            facility.name,
            ...(isSuperAdmin ? [facility.clientName] : []),
            facility.departmentID ? <DepartmentName departmentID={facility.departmentID} /> : <span className="text-light-grey">–</span>,
            facility.address?.formatted || ""
        ]
    }, [isSuperAdmin])

    const handleExport = useCallback(async (search: string, filterParams: FilterQueryParams) => {
        setExporting(true);
        try {
            // Fetch all facilities with current filters
            const params: any = {
                search: search,
                page: 1,
                limit: 10000, // Large limit to get all results
                ...filterParams,
            };

            const res = await apiV2.get<PaginatedResponse<Facility>>("facilities", { params });

            if (!res.data.items || res.data.items.length === 0) {
                alert("No facilities to export");
                return;
            }

            // Convert to Excel format - matching import format
            const excelData = res.data.items.map((facility: Facility) => ({
                id: facility.id,
                name: facility.name,
                latitude: facility.position?.coordinates?.[1] || "", // GeoJSON is [lng, lat]
                longitude: facility.position?.coordinates?.[0] || "",
                addressLine1: facility.address?.addressLines?.[0] || "",
                addressLine2: facility.address?.addressLines?.[1] || "",
                addressLine3: facility.address?.addressLines?.[2] || "",
                town: facility.address?.town || "",
                county: facility.address?.county || "",
                region: facility.address?.region || "",
                postalCode: facility.address?.postalCode || "",
                countryISO: facility.address?.countryISO || "",
                guardianIDs: facility.guardianIDs?.join(",") || "",
                clientName: facility.clientName
            }));

            // Create workbook and worksheet
            const worksheet = XLSX.utils.json_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Facilities");

            // Generate Excel file and download
            const timestamp = new Date().toISOString().split("T")[0];
            XLSX.writeFile(workbook, `facilities-export-${timestamp}.xlsx`);
        } catch (error) {
            console.error("Export failed:", error);
            alert("Failed to export facilities");
        } finally {
            setExporting(false);
        }
    }, []);

    const renderBottomActions = useCallback((search: string, filterParams: FilterQueryParams) => {
        return (
            <ButtonV2
                label={exporting ? "Exporting..." : "Export"}
                onClick={() => handleExport(search, filterParams)}
                disabled={exporting}
            />
        );
    }, [exporting, handleExport]);

    return <PaginatedSelector
        headers={headers}
        getItems={getFacilities}
        getColumns={getColumns}
        disableSelect={true}
        renderActions={renderActions}
        onColumnClick={onColumnClick}
        renderHeader={renderHeader}
        syncWithUrl={true}
        renderBottomActions={renderBottomActions}
    />
}
